/**
 * Перенос `AsyncValue<T>` из Riverpod.
 *
 * В Dart это запечатанный класс с тремя наследниками (`AsyncLoading`,
 * `AsyncData`, `AsyncError`). В TypeScript ту же роль играет размеченное
 * объединение по полю `status`: компилятор проверяет `switch` по нему так
 * же строго, как `sealed class` в Dart.
 *
 * Здесь же живёт [useAsyncValue] — замена
 * `AutoDisposeFutureProviderFamily`. Семейство провайдеров в Riverpod
 * создаётся по ключу, живёт, пока на него кто-то подписан, и уничтожается
 * вместе с последним слушателем. У хука это получается само собой:
 * состояние принадлежит компоненту и исчезает вместе с ним.
 */

import { useEffect, useRef, useState } from 'react';
import type { DependencyList } from 'react';

export type AsyncValue<T> =
  | { readonly status: 'loading' }
  | { readonly status: 'data'; readonly value: T }
  | { readonly status: 'error'; readonly error: unknown };

/**
 * `const AsyncLoading()` — состояние без данных одно на всех.
 *
 * Ссылка общая намеренно: `useState` сравнивает через `Object.is` и не
 * перерисовывает компонент, когда «загрузка» сменяется «загрузкой».
 */
export const ASYNC_LOADING: AsyncValue<never> = Object.freeze({
  status: 'loading' as const,
});

/** `AsyncData(value)` */
export function asyncData<T>(value: T): AsyncValue<T> {
  return { status: 'data', value };
}

/**
 * `AsyncError(error, stackTrace)`.
 *
 * Стек отдельным полем не хранится: в JS он принадлежит самой ошибке
 * (см. `stackTraceOf`), поэтому второй аргумент Dart-версии здесь лишний.
 */
export function asyncError<T>(error: unknown): AsyncValue<T> {
  return { status: 'error', error };
}

/**
 * `AsyncValue.valueOrNull`.
 *
 * Принимает и `null`: в Dart вызывающий писал `giffState?.valueOrNull`,
 * когда самого провайдера могло не быть (`PersonCard` без `giff`).
 */
export function valueOrNull<T>(state: AsyncValue<T> | null | undefined): T | null {
  return state !== null && state !== undefined && state.status === 'data'
    ? state.value
    : null;
}

/** Ветки `AsyncValue.when` — имена те же, что у именованных аргументов Dart. */
export interface AsyncValueMatchers<T, R> {
  readonly loading: () => R;
  readonly error: (error: unknown) => R;
  readonly data: (value: T) => R;
}

/** `state.when(loading: …, error: …, data: …)` */
export function matchAsyncValue<T, R>(
  state: AsyncValue<T>,
  matchers: AsyncValueMatchers<T, R>,
): R {
  switch (state.status) {
    case 'loading':
      return matchers.loading();
    case 'error':
      return matchers.error(state.error);
    case 'data':
      return matchers.data(state.value);
  }
}

/** Поэлементное сравнение ключей — так Riverpod сравнивает аргумент семейства. */
function depsEqual(previous: DependencyList, next: DependencyList): boolean {
  if (previous.length !== next.length) return false;
  return previous.every((item, index) => Object.is(item, next[index]));
}

/**
 * `AutoDisposeFutureProviderFamily<T, K>` одним хуком.
 *
 * Повторяет три его свойства:
 *   1. запрос уходит при монтировании и при каждой смене ключа;
 *   2. результат вытесненного запуска игнорируется — придёт он после
 *      нового ключа или после размонтирования, состояние он не тронет;
 *   3. смена ключа сразу возвращает `loading`, а не показывает данные
 *      прошлого ключа: в Riverpod это был бы другой экземпляр провайдера.
 */
export function useAsyncValue<T>(
  loader: () => Promise<T>,
  deps: DependencyList,
): AsyncValue<T> {
  const [state, setState] = useState<AsyncValue<T>>(ASYNC_LOADING);
  const [renderedDeps, setRenderedDeps] = useState<DependencyList>(deps);
  if (!depsEqual(renderedDeps, deps)) {
    // Правка состояния прямо в рендере — разрешённый React способ
    // сбросить его при смене входных данных: перерисовка случится до
    // отрисовки кадра, чужие данные на экран не попадут.
    setRenderedDeps(deps);
    setState(ASYNC_LOADING);
  }

  // Загрузчик — стрелка из тела компонента, её ссылка меняется каждый
  // рендер. Перезапускать запрос надо только по ключу, поэтому до
  // загрузчика эффект добирается через ref.
  const loaderRef = useRef(loader);
  useEffect(() => {
    loaderRef.current = loader;
  });

  useEffect(() => {
    let active = true;
    void (async (): Promise<void> => {
      try {
        const value = await loaderRef.current();
        if (!active) return;
        setState(asyncData(value));
      } catch (error) {
        if (!active) return;
        setState(asyncError(error));
      }
    })();
    return () => {
      active = false;
    };
    // Массив зависимостей приходит от вызывающего — это и есть ключ семейства.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  return state;
}
